import React, { useEffect, useRef, useState } from 'react';

// Live view of an Evaluate run: one column per council agent (plus the coordinator),
// streaming each step — the agent's visible reasoning, tool calls, results and A2A hops.
// Polls /api/evaluate-live/<job_id>?since=N until the job finishes.
const KIND = {
  received: { icon: '📥', color: '#4a5568', label: 'received' },
  thought: { icon: '💭', color: '#6f42c1', label: 'thinking' },
  action: { icon: '🛠️', color: '#0073bb', label: 'action' },
  observation: { icon: '👁️', color: '#2f855a', label: 'observed' },
  a2a_send: { icon: '📡', color: '#b7791f', label: 'A2A' },
  result: { icon: '✅', color: '#155724', label: 'verdict' },
  info: { icon: 'ℹ️', color: '#4a5568', label: 'info' },
  error: { icon: '⚠️', color: '#b00020', label: 'error' },
  done: { icon: '🏁', color: '#155724', label: 'done' },
};

export default function CouncilTracePanel({ jobId, headers, onDone, compact = false }) {
  const [job, setJob] = useState(null);
  const [events, setEvents] = useState([]);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all');
  const sinceRef = useRef(0);
  const doneRef = useRef(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const headersRef = useRef(headers);   // parent recreates this every render; don't restart polling for it
  headersRef.current = headers;

  useEffect(() => {
    if (!jobId) return undefined;
    let stop = false;
    sinceRef.current = 0; doneRef.current = false; setEvents([]); setJob(null); setErr('');
    const tick = async () => {
      if (stop) return;
      try {
        const r = await fetch(`/api/evaluate-live/${jobId}?since=${sinceRef.current}`, { headers: headersRef.current() });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) { setErr(d.error || `HTTP ${r.status}`); return; }
        if (d.events && d.events.length) setEvents(prev => prev.concat(d.events));
        sinceRef.current = d.next || sinceRef.current;
        setJob(d);
        if (d.status !== 'RUNNING') {
          if (!doneRef.current) { doneRef.current = true; if (onDoneRef.current) onDoneRef.current(d); }
          return;
        }
      } catch (e) { setErr('Lost connection while polling the evaluation.'); }
      if (!stop) setTimeout(tick, 1000);
    };
    tick();
    return () => { stop = true; };
  }, [jobId]);

  // Compact mode (Admin › LLM Council › "Show council details on the scorecard" OFF):
  // same polling, but only a progress line — no judge names, votes or reasoning.
  if (compact) {
    if (job && job.status !== 'RUNNING' && !err && job.status !== 'FAILED') return null;
    return (
      <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px 10px', background: '#fbfcfe', marginTop: '10px', fontSize: '12px', color: '#4a5568' }}>
        {err ? <span style={{ color: '#b00020' }}>{err}</span>
          : job && job.status === 'FAILED' ? <span style={{ color: '#b00020' }}>Evaluation failed: {String(job.error || '')}</span>
          : <span>⏳ Running the golden test suite on the real model{events.length ? ` · ${events[events.length - 1].t}s` : ''}…</span>}
      </div>
    );
  }

  const agents = ['coordinator', ...Array.from(new Set(events.map(e => e.agent).filter(a => a !== 'coordinator')))];
  const shown = (a) => events.filter(e => e.agent === a && (filter === 'all' || e.kind === filter
    || (filter === 'action' && (e.kind === 'a2a_send' || e.kind === 'observation'))));
  const verdicts = (a) => events.filter(e => e.agent === a && e.kind === 'result').length;

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px', background: '#fbfcfe', marginTop: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#0f2027' }}>
          🧑‍⚖️ LLM council — live trace
          <span style={{ fontWeight: 'normal', color: '#718096', marginLeft: '8px', fontSize: '11px' }}>
            {job ? `${job.mode === 'a2a' ? 'A2A agents on AgentCore' : 'direct Bedrock judges'} · ${job.status}` : 'starting…'}
            {events.length ? ` · ${events.length} steps · ${events[events.length - 1].t}s` : ''}
          </span>
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)} style={{ fontSize: '11px', padding: '3px' }}>
          <option value="all">All steps</option>
          <option value="thought">Thinking only</option>
          <option value="action">Actions & A2A</option>
          <option value="result">Verdicts only</option>
          <option value="error">Errors only</option>
        </select>
      </div>
      {err && <div style={{ color: '#b00020', fontSize: '12px', marginBottom: '6px' }}>{err}</div>}
      {job && job.status === 'FAILED' && <div style={{ color: '#b00020', fontSize: '12px', marginBottom: '6px' }}>Evaluation failed: {String(job.error || '')}</div>}
      <div style={{ display: 'flex', gap: '8px', overflowX: 'auto' }}>
        {agents.map(a => (
          <div key={a} style={{ flex: '1 0 260px', minWidth: '260px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '6px 8px', borderBottom: '1px solid #edf2f7', fontSize: '12px', fontWeight: 'bold',
                          background: a === 'coordinator' ? '#eef6fc' : '#f7f3ff', color: a === 'coordinator' ? '#0073bb' : '#6f42c1' }}>
              {a === 'coordinator' ? '🧭 Coordinator (EEA backend)' : `🤖 Judge: ${a}`}
              {a !== 'coordinator' && <span style={{ fontWeight: 'normal', color: '#718096' }}> · {verdicts(a)} verdicts</span>}
            </div>
            <div style={{ maxHeight: '360px', overflowY: 'auto', padding: '6px 8px' }}>
              {shown(a).length === 0 && <div style={{ fontSize: '11px', color: '#a0aec0' }}>{job && job.status === 'RUNNING' ? 'waiting…' : 'no steps'}</div>}
              {shown(a).map((e, i) => {
                const k = KIND[e.kind] || KIND.info;
                return (
                  <div key={i} style={{ fontSize: '11px', marginBottom: '6px', borderLeft: `3px solid ${k.color}`, paddingLeft: '6px' }}>
                    <div style={{ color: k.color, fontWeight: 'bold' }}>{k.icon} {k.label} <span style={{ color: '#a0aec0', fontWeight: 'normal' }}>{e.t}s</span></div>
                    <div style={{ color: '#2d3748', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{e.text}</div>
                    {e.tool && e.input && Object.keys(e.input).length > 0 && (
                      <code style={{ display: 'block', fontSize: '10px', color: '#4a5568', background: '#f7fafc', padding: '2px 4px', marginTop: '2px', whiteSpace: 'pre-wrap' }}>
                        {e.tool}({JSON.stringify(e.input)})
                      </code>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: '10px', color: '#a0aec0', marginTop: '6px' }}>
        "Thinking" is each judge's visible reasoning and tool calls, as returned by the model — not hidden chain-of-thought.
      </div>
    </div>
  );
}
